/**
 * Auto-generated Shopify discount codes for Freebi campaigns.
 *
 * Formats:
 * - Spend unlock / quantity unlock free gift → FREEBIE_XXXXX
 * - Buy X Get Y → BUYXGETY_XXXXX
 * - Milestone rewards → MILESTONE_XXXXX
 *
 * XXXXX = 5 alphanumeric chars (A–Z, 0–9), unique per store.
 */

export type DiscountCodePrefix = 'FREEBIE_' | 'BUYXGETY_' | 'MILESTONE_';

const ALPHANUM = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I,O,0,1 — clearer codes

export function randomCodeSuffix(length = 5): string {
  let out = '';
  const cryptoObj =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint8Array(length);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < length; i++) {
      out += ALPHANUM[buf[i]! % ALPHANUM.length];
    }
    return out;
  }
  for (let i = 0; i < length; i++) {
    out += ALPHANUM[Math.floor(Math.random() * ALPHANUM.length)]!;
  }
  return out;
}

export function generateDiscountCode(prefix: DiscountCodePrefix): string {
  return `${prefix}${randomCodeSuffix(5)}`;
}

/** Free gift (spend X or add X items) */
export function generateFreebieCode(): string {
  return generateDiscountCode('FREEBIE_');
}

/** Buy X Get Y */
export function generateBuyXGetYCode(): string {
  return generateDiscountCode('BUYXGETY_');
}

/** Milestone rewards */
export function generateMilestoneCode(): string {
  return generateDiscountCode('MILESTONE_');
}

/**
 * Generate a code that is not in `taken` (case-insensitive).
 * Retries until unique (max 40 attempts).
 */
export function generateUniqueDiscountCode(
  prefix: DiscountCodePrefix,
  taken: Iterable<string>,
): string {
  const used = new Set(
    [...taken].map((c) => String(c || '').trim().toUpperCase()).filter(Boolean),
  );
  for (let i = 0; i < 40; i++) {
    const code = generateDiscountCode(prefix);
    if (!used.has(code.toUpperCase())) return code;
  }
  // Extremely unlikely fallback
  return `${prefix}${randomCodeSuffix(5)}${Date.now().toString(36).slice(-3).toUpperCase()}`;
}

/** Pull discount codes out of a campaign config JSON blob. */
export function extractCodesFromConfig(config: unknown): string[] {
  if (!config || typeof config !== 'object') return [];
  const cfg = config as Record<string, unknown>;
  const codes: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === 'string' && v.trim()) codes.push(v.trim().toUpperCase());
  };

  const fg = (cfg.freeGift || {}) as Record<string, unknown>;
  const fgDisc = (fg.discount || {}) as Record<string, unknown>;
  push(fgDisc.code);

  const bx = (cfg.buyXGetY || {}) as Record<string, unknown>;
  const bxDisc = (bx.discount || {}) as Record<string, unknown>;
  push(bxDisc.code);

  const disc = (cfg.discount || {}) as Record<string, unknown>;
  push(disc.code);

  // Milestone nested codes (order / shipping / gift arrays)
  const nested = disc.codes as
    | {
        order?: Array<{ code?: string }>;
        shipping?: Array<{ code?: string }>;
        gift?: Array<{ code?: string }>;
      }
    | undefined;
  if (nested) {
    for (const arr of [nested.order, nested.shipping, nested.gift]) {
      for (const row of arr || []) push(row?.code);
    }
  }

  return [...new Set(codes)];
}

/**
 * Ensure `code` is unique among `taken`. If taken, generate a new one with `prefix`.
 */
export function ensureUniqueCode(
  code: string,
  prefix: DiscountCodePrefix,
  taken: Iterable<string>,
  opts?: { excludeSelf?: string },
): string {
  const self = (opts?.excludeSelf || '').trim().toUpperCase();
  const used = new Set(
    [...taken]
      .map((c) => String(c || '').trim().toUpperCase())
      .filter((c) => c && c !== self),
  );
  const normalized = (code || '').trim().toUpperCase().replace(/\s+/g, '');
  if (normalized && !used.has(normalized)) return normalized;
  return generateUniqueDiscountCode(prefix, used);
}

type ApiLike = {
  get: <T = unknown>(path: string) => Promise<T>;
};

/**
 * Load other campaigns' codes and return a unique code for save.
 * `excludeCampaignId` = current campaign when editing (own code is allowed).
 */
export async function resolveUniqueDiscountCode(
  api: ApiLike,
  prefix: DiscountCodePrefix,
  proposed: string,
  excludeCampaignId?: string,
): Promise<string> {
  try {
    const res = await api.get<{
      items?: Array<{ id?: string; config?: unknown }>;
    }>('/campaigns?limit=100');
    const taken: string[] = [];
    for (const item of res.items || []) {
      if (excludeCampaignId && item.id === excludeCampaignId) continue;
      taken.push(...extractCodesFromConfig(item.config));
    }
    return ensureUniqueCode(proposed, prefix, taken, {
      excludeSelf: proposed,
    });
  } catch {
    // Offline / API error — still return a well-formed code
    const n = (proposed || '').trim().toUpperCase();
    if (n) return n;
    return generateDiscountCode(prefix);
  }
}
